import { mulberry32, dateToSeed } from './seededRandom';

/**
 * Daily picker — deterministic-from-date sampler with two heuristics
 * layered on uniform random:
 *
 *   1. **Within-day country diversity** — once today's slate already
 *      contains a city in country X, further candidates from country X
 *      are downweighted by `weights.sameCountry` (default 0.10 → ~10×
 *      less likely than a fresh-country candidate). Prevents
 *      "all-China day" outcomes that pure uniform sampling produces
 *      from a city pool with heavy country-skew, while still leaving a
 *      small chance for a same-country pair when it happens to fit.
 *
 *   2. **Cross-day cooldown** — cities that appear in the past
 *      `cooldownDays` days' picks (default 14) are downweighted by
 *      `weights.recent` (default 0.05 → ~20× less likely). Two-day
 *      Tokyo repeats become "real eyebrow-raisers" rather than routine
 *      coincidence.
 *
 * Both penalties stack multiplicatively: a city that's BOTH
 * same-country AND in the cooldown window gets weight 0.10 × 0.05 =
 * 0.005, effectively excluded.
 *
 * Determinism is hard-required for the daily mode — two players in
 * different timezones playing "today" must see the same puzzle, past
 * dates must reproduce identically forever, and we can't add a server
 * round-trip. So the cooldown avoid-set is itself derived from the
 * date by re-running the picker for past days *without* the cooldown
 * step (see `pickDailyCitiesNoCooldown`). This caps the recursion and
 * keeps the past-day computation bounded at O(cooldownDays) extra
 * picker runs per call.
 *
 * The slight semantic price: the avoid-set references the no-cooldown
 * versions of past days, not what those days actually picked under
 * the cooldown rule. Players never notice (they only see today's
 * puzzle), and it lets us guarantee stability of `pickDailyCities(d)`
 * for any d — call it from any other day's compute or from a future
 * "what was day d?" lookup, get the same picks.
 *
 * Generic over the item type (`countryOf` / `idOf` projections) so a
 * future country-daily mode can reuse the same algorithm without
 * coupling it to the City schema.
 */

/** Default penalty multipliers. Tuned for a ~1500-city pool with
 *  heavy country-skew (China + India + US dominate); revisit if pool
 *  shape changes. Values are exposed as constants so the picker tests
 *  + the call site share one source of truth. */
export const DEFAULT_DAILY_PICKER_WEIGHTS = {
  /** Multiplier applied when a candidate's country is already in
   *  today's slate. 0.10 = ~10× less likely than a fresh-country
   *  candidate. Set to 1 to disable the within-day country heuristic. */
  sameCountry: 0.1,
  /** Multiplier applied when a candidate appears in the past
   *  `cooldownDays` days' picks. 0.05 = ~20× less likely. Set to 1 to
   *  disable the cross-day cooldown heuristic; set to 0 for hard
   *  exclusion (not recommended — small pools paint into a corner). */
  recent: 0.05,
} as const;

/** Default cooldown window. 14 days matches the user-facing intuition
 *  "two weeks before I might see Tokyo again." */
export const DEFAULT_DAILY_PICKER_COOLDOWN_DAYS = 14;

export interface PickDailyCitiesOptions<T> {
  /** Eligible item pool — already filtered by mode (tier, region, etc.). */
  pool: readonly T[];
  /** How many items to pick for the day. Daily uses 5. */
  count: number;
  /** Date the picks are FOR, in `YYYY-MM-DD` form. Drives the seed
   *  AND the cooldown lookback range. */
  date: string;
  /** Stable id projection. Two cities with the same id are treated
   *  as the same item for de-duplication and cooldown matching. */
  idOf: (item: T) => string;
  /** Country projection. Items with the same country are clustered
   *  by the within-day diversity heuristic. */
  countryOf: (item: T) => string;
  /** Days back to look for the cooldown avoid-set. Default 14. Pass
   *  0 to disable the cross-day heuristic entirely. */
  cooldownDays?: number;
  /** Penalty multipliers; missing keys fall back to defaults. */
  weights?: Partial<typeof DEFAULT_DAILY_PICKER_WEIGHTS>;
}

/**
 * Pick `count` items for one daily date with country diversity +
 * cross-day cooldown applied. Same input → same output, forever.
 */
export function pickDailyCities<T>(opts: PickDailyCitiesOptions<T>): T[] {
  const cooldownDays = opts.cooldownDays ?? DEFAULT_DAILY_PICKER_COOLDOWN_DAYS;
  const weights = { ...DEFAULT_DAILY_PICKER_WEIGHTS, ...opts.weights };

  // Build the cooldown avoid-set from past N days. Each past day uses
  // the no-cooldown variant so the recursion is bounded at one level
  // — see file-level comment for the semantic trade-off.
  const avoid = new Set<string>();
  for (let k = 1; k <= cooldownDays; k++) {
    const past = subtractDays(opts.date, k);
    const pastPicks = pickDailyCitiesNoCooldown({
      pool: opts.pool,
      count: opts.count,
      date: past,
      idOf: opts.idOf,
      countryOf: opts.countryOf,
      weights: { sameCountry: weights.sameCountry },
    });
    for (const p of pastPicks) avoid.add(opts.idOf(p));
  }

  return pickWithWeights({
    pool: opts.pool,
    count: opts.count,
    date: opts.date,
    idOf: opts.idOf,
    countryOf: opts.countryOf,
    avoid,
    weights,
  });
}

/**
 * Variant used to populate the cooldown avoid-set for past dates.
 * Applies within-day country diversity but skips the cross-day step.
 *
 * Exported because the tests want to assert "this is the past-day
 * approximation, not what that day actually picked" without
 * re-implementing the algorithm in the test file.
 */
export function pickDailyCitiesNoCooldown<T>(opts: {
  pool: readonly T[];
  count: number;
  date: string;
  idOf: (item: T) => string;
  countryOf: (item: T) => string;
  weights?: { sameCountry: number };
}): T[] {
  return pickWithWeights({
    pool: opts.pool,
    count: opts.count,
    date: opts.date,
    idOf: opts.idOf,
    countryOf: opts.countryOf,
    avoid: EMPTY_SET,
    weights: {
      sameCountry: opts.weights?.sameCountry ?? DEFAULT_DAILY_PICKER_WEIGHTS.sameCountry,
      // recent doesn't apply when avoid is empty.
      recent: 1,
    },
  });
}

/** Single shared empty Set for the no-cooldown path so we don't
 *  allocate a fresh one on every past-day computation. */
const EMPTY_SET: ReadonlySet<string> = new Set();

/**
 * Inner picker — pulls `count` items applying the weight rules. All
 * three callers (full picker, no-cooldown variant, future overrides)
 * share this so the weight semantics are pinned in one place.
 */
function pickWithWeights<T>(opts: {
  pool: readonly T[];
  count: number;
  date: string;
  idOf: (item: T) => string;
  countryOf: (item: T) => string;
  avoid: ReadonlySet<string>;
  weights: { sameCountry: number; recent: number };
}): T[] {
  const rng = mulberry32(dateToSeed(opts.date));
  const picked: T[] = [];
  const pickedIds = new Set<string>();
  const pickedCountries = new Set<string>();

  for (let i = 0; i < opts.count; i++) {
    const choice = weightedPick(opts.pool, rng, (item) => {
      const id = opts.idOf(item);
      // Hard-exclude already-picked-today items so a single day's
      // slate never contains two of the same city even with extreme
      // weight imbalance. Other duplicates (same country, recent)
      // get downweighted but remain possible.
      if (pickedIds.has(id)) return 0;
      let w = 1;
      if (pickedCountries.has(opts.countryOf(item))) w *= opts.weights.sameCountry;
      if (opts.avoid.has(id)) w *= opts.weights.recent;
      return w;
    });
    if (!choice) break;
    picked.push(choice);
    pickedIds.add(opts.idOf(choice));
    pickedCountries.add(opts.countryOf(choice));
  }
  return picked;
}

/**
 * Cumulative-distribution weighted sampler. Returns null only when
 * every item in the pool has weight ≤ 0 (e.g. pool exhausted by
 * picked-ids hard-exclusion).
 *
 * O(N) per pick which is fine at N≈1500 × count=5 — ~7.5k ops per
 * day, totally negligible.
 */
function weightedPick<T>(
  pool: readonly T[],
  rng: () => number,
  weightOf: (item: T) => number,
): T | null {
  // Single pass: build the cumulative distribution while summing.
  // Saves a second loop over the pool.
  const cum = new Array<number>(pool.length);
  let total = 0;
  for (let i = 0; i < pool.length; i++) {
    const w = Math.max(0, weightOf(pool[i]));
    total += w;
    cum[i] = total;
  }
  if (total <= 0) return null;
  const r = rng() * total;
  // Linear scan — could binary-search the cumulative array for
  // O(log N), but the constant factor of binary search isn't worth
  // it at 5 picks per call.
  for (let i = 0; i < pool.length; i++) {
    if (r < cum[i]) return pool[i];
  }
  // Floating-point safety: if rng() returned a value just below 1 and
  // cumulative summing accumulated rounding error, r might exceed the
  // final cum[]. Fall back to the last weighted item.
  for (let i = pool.length - 1; i >= 0; i--) {
    if (cum[i] > (i === 0 ? 0 : cum[i - 1])) return pool[i];
  }
  return null;
}

/**
 * Subtract `k` days from a `YYYY-MM-DD` string, returning a string of
 * the same shape. Uses the local-date `Date` constructor so DST and
 * month/year boundaries fall out for free.
 *
 * Exported so the picker tests can assert the boundary cases without
 * duplicating the implementation.
 */
export function subtractDays(yyyymmdd: string, k: number): string {
  const [y, m, d] = yyyymmdd.split('-').map(Number);
  const dt = new Date(y, m - 1, d);
  dt.setDate(dt.getDate() - k);
  const yy = dt.getFullYear();
  const mm = String(dt.getMonth() + 1).padStart(2, '0');
  const dd = String(dt.getDate()).padStart(2, '0');
  return `${yy}-${mm}-${dd}`;
}
